import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  createMarketComment,
  getMarketComments,
  hideMarketComment,
  reportMarketComment,
  toggleMarketCommentLike
} from "../lib/api";

const COMMENT_ERROR = "Could not load market comments.";

function reusableCommentsData(previousData, marketId) {
  return previousData?.marketId === marketId ? previousData : null;
}

export function buildCommentsLoadingState(previous, marketId) {
  const data = reusableCommentsData(previous?.data, marketId);
  return {
    status: data ? "refreshing" : "loading",
    data,
    error: ""
  };
}

export function buildCommentsErrorState(previous, marketId, error) {
  const data = reusableCommentsData(previous?.data, marketId);
  return {
    status: "error",
    data,
    error: error?.message || COMMENT_ERROR
  };
}

export function mergeCreatedComment(data, comment) {
  return {
    ...(data || {}),
    comments: [comment, ...(data?.comments || []).filter((item) => item.id !== comment.id)]
  };
}

export function replaceCommentReaction(data, reaction) {
  return {
    ...(data || {}),
    comments: (data?.comments || []).map((comment) =>
      comment.id === reaction.commentId
        ? { ...comment, likeCount: reaction.likeCount, viewerLiked: reaction.viewerLiked }
        : comment
    )
  };
}

export function appendCommentsPage(current, page) {
  if (!current || String(current.marketId || "") !== String(page?.marketId || "")) return current;

  const existingComments = Array.isArray(current.comments) ? current.comments : [];
  const seenIds = new Set(existingComments.map((comment) => String(comment?.id || "")).filter(Boolean));
  const incomingComments = (Array.isArray(page?.comments) ? page.comments : []).filter((comment) => {
    const id = String(comment?.id || "");
    if (!id) return true;
    if (seenIds.has(id)) return false;
    seenIds.add(id);
    return true;
  });

  return {
    ...current,
    ...page,
    comments: [...existingComments, ...incomingComments],
    nextCursor: page?.nextCursor || null
  };
}

export function applyCommentsResultForMarket(previous, expectedMarketId, currentMarketId, nextData) {
  if (!isCurrentMarketUpdate({ expectedMarketId, currentMarketId })) return previous;
  if (String(nextData?.marketId || "") !== String(expectedMarketId || "")) return previous;

  return { status: "ready", data: nextData, error: "" };
}

export function applyCommentsPageForMarket(previous, expectedMarketId, currentMarketId, nextData) {
  if (!canApplyActionUpdate(previous, expectedMarketId, currentMarketId)) return previous;
  if (String(nextData?.marketId || "") !== String(expectedMarketId || "")) return previous;

  return {
    ...previous,
    status: "ready",
    data: appendCommentsPage(previous.data, nextData),
    error: ""
  };
}

export function applyCreatedCommentForMarket(previous, expectedMarketId, currentMarketId, comment) {
  if (!canApplyActionUpdate(previous, expectedMarketId, currentMarketId)) return previous;
  if (String(comment?.marketId || "") !== String(expectedMarketId || "")) return previous;

  return {
    ...previous,
    status: "ready",
    data: mergeCreatedComment(previous.data, comment),
    error: ""
  };
}

export function applyReactionForMarket(previous, expectedMarketId, currentMarketId, reaction) {
  if (!canApplyActionUpdate(previous, expectedMarketId, currentMarketId)) return previous;

  return {
    ...previous,
    data: replaceCommentReaction(previous.data, reaction)
  };
}

export function applyHiddenCommentForMarket(previous, expectedMarketId, currentMarketId, hidden) {
  if (!canApplyActionUpdate(previous, expectedMarketId, currentMarketId)) return previous;
  if (String(hidden?.marketId || "") !== String(expectedMarketId || "")) return previous;

  return {
    ...previous,
    data: {
      ...previous.data,
      comments: (previous.data?.comments || []).map((comment) => (comment.id === hidden.id ? hidden : comment))
    }
  };
}

export function useMarketComments({ marketId, sort } = {}) {
  const [state, setState] = useState({ status: "idle", data: null, error: "" });
  const normalizedMarketId = String(marketId || "");
  const normalizedSort = sort || "top";
  const latestMarketIdRef = useRef(normalizedMarketId);
  const stateRef = useRef(state);
  latestMarketIdRef.current = normalizedMarketId;
  stateRef.current = state;

  const refresh = useCallback(() => {
    if (!normalizedMarketId) {
      setState({ status: "idle", data: null, error: "" });
      return Promise.resolve();
    }

    setState((previous) => buildCommentsLoadingState(previous, normalizedMarketId));
    return getMarketComments({ marketId: normalizedMarketId, sort: normalizedSort })
      .then((data) => {
        setState((previous) =>
          applyCommentsResultForMarket(previous, normalizedMarketId, latestMarketIdRef.current, data)
        );
        return data;
      })
      .catch((error) => {
        setState((previous) =>
          isCurrentMarketUpdate({ expectedMarketId: normalizedMarketId, currentMarketId: latestMarketIdRef.current })
            ? buildCommentsErrorState(previous, normalizedMarketId, error)
            : previous
        );
        return null;
      });
  }, [normalizedMarketId, normalizedSort]);

  useEffect(() => {
    if (!normalizedMarketId) {
      setState({ status: "idle", data: null, error: "" });
      return;
    }

    let cancelled = false;
    setState((previous) => buildCommentsLoadingState(previous, normalizedMarketId));

    getMarketComments({ marketId: normalizedMarketId, sort: normalizedSort })
      .then((data) => {
        if (cancelled) return;
        setState((previous) =>
          applyCommentsResultForMarket(previous, normalizedMarketId, latestMarketIdRef.current, data)
        );
      })
      .catch((error) => {
        if (cancelled) return;
        setState((previous) =>
          isCurrentMarketUpdate({ expectedMarketId: normalizedMarketId, currentMarketId: latestMarketIdRef.current })
            ? buildCommentsErrorState(previous, normalizedMarketId, error)
            : previous
        );
      });

    return () => {
      cancelled = true;
    };
  }, [normalizedMarketId, normalizedSort]);

  const actions = useMemo(
    () => ({
      loadMore: async () => {
        const cursor = stateRef.current?.data?.nextCursor;
        if (!normalizedMarketId || !cursor) return null;
        setState((previous) =>
          canApplyActionUpdate(previous, normalizedMarketId, latestMarketIdRef.current)
            ? { ...previous, status: "refreshing", error: "" }
            : previous
        );
        return getMarketComments({ marketId: normalizedMarketId, sort: normalizedSort, cursor })
          .then((data) => {
            setState((previous) =>
              applyCommentsPageForMarket(previous, normalizedMarketId, latestMarketIdRef.current, data)
            );
            return data;
          })
          .catch((error) => {
            setState((previous) =>
              isCurrentMarketUpdate({ expectedMarketId: normalizedMarketId, currentMarketId: latestMarketIdRef.current })
                ? buildCommentsErrorState(previous, normalizedMarketId, error)
                : previous
            );
            return null;
          });
      },
      create: async (body) => {
        const comment = await createMarketComment({ marketId: normalizedMarketId, body });
        setState((previous) =>
          applyCreatedCommentForMarket(previous, normalizedMarketId, latestMarketIdRef.current, comment)
        );
        return comment;
      },
      like: async (commentId) => {
        const reaction = await toggleMarketCommentLike({ marketId: normalizedMarketId, commentId });
        setState((previous) =>
          applyReactionForMarket(previous, normalizedMarketId, latestMarketIdRef.current, reaction)
        );
        return reaction;
      },
      hide: async (commentId) => {
        const hidden = await hideMarketComment({ marketId: normalizedMarketId, commentId });
        setState((previous) =>
          applyHiddenCommentForMarket(previous, normalizedMarketId, latestMarketIdRef.current, hidden)
        );
        return hidden;
      },
      report: (commentId, input) => reportMarketComment({ marketId: normalizedMarketId, commentId, ...input })
    }),
    [normalizedMarketId, normalizedSort]
  );

  return { ...state, refresh, actions };
}

function isCurrentMarketUpdate({ expectedMarketId, currentMarketId }) {
  const expected = String(expectedMarketId || "");
  return Boolean(expected) && expected === String(currentMarketId || "");
}

function canApplyActionUpdate(previous, expectedMarketId, currentMarketId) {
  return (
    isCurrentMarketUpdate({ expectedMarketId, currentMarketId }) &&
    String(previous?.data?.marketId || "") === String(expectedMarketId || "")
  );
}
